/**
 * ADR-005 / §5.2 — the floor map is vector scene data, so one immutable
 * document drives the editor, the live operational view and (later) heatmaps.
 *
 * Everything here is pure: an operation takes a `Scene` and returns a new one,
 * which is what lets the history stack keep whole snapshots and compare them by
 * reference.
 */
import { MapElementType } from '@/lib/shared-types';
import { mapElementColors } from '@/lib/ui-tokens';
import {
  DEFAULT_FLOOR_HEIGHT,
  DEFAULT_FLOOR_WIDTH,
  cornerRadius,
  elementDefaultSize,
  elementFromServer,
  isLinkedTable,
  sceneFromFloor,
  type FloorScene,
  type MapEntityType,
  type Scene,
  type SceneElement,
  type ServerMapElement,
} from '@/lib/domain';

/**
 * The scene *reader* is shared with the waiter app (`@/lib/domain`), because
 * both draw the same `GET /floor-map` document and two readers would drift.
 * What stays here is everything only an editor needs: history-friendly pure
 * operations, hit-testing, resize/rotate geometry and the placement rules.
 */
export {
  DEFAULT_FLOOR_HEIGHT,
  DEFAULT_FLOOR_WIDTH,
  cornerRadius,
  elementDefaultSize,
  elementFromServer,
  isLinkedTable,
  sceneFromFloor,
  type FloorScene,
  type MapEntityType,
  type Scene,
  type SceneElement,
  type ServerMapElement,
};

/** The map palette, shared so a room is the same colour on every screen. */
export const elementFill = mapElementColors;


// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

const LOCAL_PREFIX = 'local:';
let localSequence = 0;

export function newLocalId(): string {
  localSequence += 1;
  return `${LOCAL_PREFIX}${localSequence}`;
}

/** A local id never reaches the API — the bulk upsert creates those rows. */
export function isLocalId(id: string): boolean {
  return id.startsWith(LOCAL_PREFIX);
}

export function newGroupId(): string {
  localSequence += 1;
  return `g${Date.now().toString(36)}${localSequence}`;
}

// ---------------------------------------------------------------------------
// Per-type presentation. Every colour comes from @/lib/ui-tokens.
// ---------------------------------------------------------------------------

export const MAP_ELEMENT_TYPES = Object.values(MapElementType);

export function isLinkable(type: MapElementType): boolean {
  return type === MapElementType.TABLE || type === MapElementType.RESOURCE;
}

export function entityTypeFor(type: MapElementType): MapEntityType | null {
  if (type === MapElementType.TABLE) return 'VenueTable';
  if (type === MapElementType.RESOURCE) return 'Resource';
  return null;
}

// ---------------------------------------------------------------------------
// Server ⇄ scene
// ---------------------------------------------------------------------------

const round = (value: number) => Math.round(value * 100) / 100;

/**
 * One element in the shape `PUT /floor-map/:floorId/elements` accepts. The
 * global ValidationPipe runs with `forbidNonWhitelisted`, so nothing that is
 * not a DTO property may appear.
 *
 * `entityType`/`entityId` are always sent, `null` included. Omitting them looks
 * like "leave this alone" to a Prisma update, so an element that was unlinked in
 * the editor came back linked on the next load — the link has to be cleared
 * explicitly for §38 to hold in both directions.
 */
export function elementPayload(element: SceneElement): Record<string, unknown> {
  const metadata: Record<string, unknown> = { ...(element.metadataJson ?? {}) };
  delete metadata.groupId;
  delete metadata.label;
  if (element.groupId) metadata.groupId = element.groupId;
  if (element.label) metadata.label = element.label;

  const linked = Boolean(element.entityType && element.entityId);

  return {
    ...(isLocalId(element.id) ? {} : { id: element.id }),
    elementType: element.elementType,
    x: round(element.x),
    y: round(element.y),
    width: round(element.width),
    height: round(element.height),
    rotation: round(element.rotation),
    zIndex: element.zIndex,
    locked: element.locked,
    visible: element.visible,
    entityType: linked ? element.entityType : null,
    entityId: linked ? element.entityId : null,
    ...(element.styleJson ? { styleJson: element.styleJson } : {}),
    // Always sent, so clearing a group or a label actually persists.
    metadataJson: metadata,
  };
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function snapTo(value: number, grid: number, enabled: boolean): number {
  return enabled && grid > 0 ? Math.round(value / grid) * grid : value;
}

export function centerOf(element: Rect): Point {
  return { x: element.x + element.width / 2, y: element.y + element.height / 2 };
}

export function rotatePoint(point: Point, origin: Point, degrees: number): Point {
  const angle = (degrees * Math.PI) / 180;
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  return {
    x: origin.x + dx * Math.cos(angle) - dy * Math.sin(angle),
    y: origin.y + dx * Math.sin(angle) + dy * Math.cos(angle),
  };
}

/** Scene point → the element's own unrotated frame. */
export function toElementFrame(point: Point, element: SceneElement): Point {
  return rotatePoint(point, centerOf(element), -element.rotation);
}

export type HandleId = 'nw' | 'ne' | 'se' | 'sw' | 'rotate';

const MIN_SIZE = 8;

/**
 * Resize from one corner. The maths runs in the element's unrotated frame and
 * then shifts x/y back so the opposite corner stays put on screen — without
 * that correction a rotated element walks away from the pointer.
 */
export function resizeFromCorner(
  element: SceneElement,
  handle: Exclude<HandleId, 'rotate'>,
  pointer: Point,
  grid: number,
  snap: boolean,
): SceneElement {
  const local = toElementFrame(pointer, element);
  const anchor: Point = {
    x: handle === 'nw' || handle === 'sw' ? element.x + element.width : element.x,
    y: handle === 'nw' || handle === 'ne' ? element.y + element.height : element.y,
  };

  const moved: Point = {
    x: snapTo(local.x, grid, snap),
    y: snapTo(local.y, grid, snap),
  };

  const left = Math.min(anchor.x, moved.x);
  const top = Math.min(anchor.y, moved.y);
  const width = Math.max(MIN_SIZE, Math.abs(moved.x - anchor.x));
  const height = Math.max(MIN_SIZE, Math.abs(moved.y - anchor.y));

  const before = centerOf(element);
  const after = centerOf({ x: left, y: top, width, height });
  const anchorBefore = rotatePoint(anchor, before, element.rotation);
  const anchorAfter = rotatePoint(anchor, after, element.rotation);

  return {
    ...element,
    x: left + (anchorBefore.x - anchorAfter.x),
    y: top + (anchorBefore.y - anchorAfter.y),
    width,
    height,
  };
}

export function boundsOf(elements: readonly SceneElement[]): Rect | null {
  if (elements.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const element of elements) {
    const center = centerOf(element);
    for (const corner of [
      { x: element.x, y: element.y },
      { x: element.x + element.width, y: element.y },
      { x: element.x + element.width, y: element.y + element.height },
      { x: element.x, y: element.y + element.height },
    ]) {
      const p = rotatePoint(corner, center, element.rotation);
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
  );
}

// ---------------------------------------------------------------------------
// Scene operations — each returns a new document
// ---------------------------------------------------------------------------

export function mapElements(
  scene: Scene,
  ids: readonly string[],
  update: (element: SceneElement) => SceneElement,
): Scene {
  const set = new Set(ids);
  return {
    ...scene,
    elements: scene.elements.map((element) => (set.has(element.id) ? update(element) : element)),
  };
}

export function patchElements(
  scene: Scene,
  ids: readonly string[],
  patch: Partial<SceneElement>,
): Scene {
  return mapElements(scene, ids, (element) => ({ ...element, ...patch }));
}

export function addElements(scene: Scene, elements: readonly SceneElement[]): Scene {
  return { ...scene, elements: [...scene.elements, ...elements] };
}

export function removeElements(scene: Scene, ids: readonly string[]): Scene {
  const set = new Set(ids);
  return { ...scene, elements: scene.elements.filter((element) => !set.has(element.id)) };
}

export function topZIndex(scene: Scene): number {
  return scene.elements.reduce((max, element) => Math.max(max, element.zIndex), 0);
}

export function bottomZIndex(scene: Scene): number {
  return scene.elements.reduce((min, element) => Math.min(min, element.zIndex), 0);
}

export function createElement(
  type: MapElementType,
  at: Point,
  scene: Scene,
  grid: number,
  snap: boolean,
): SceneElement {
  const size = elementDefaultSize[type];
  return {
    id: newLocalId(),
    elementType: type,
    entityType: null,
    entityId: null,
    x: snapTo(at.x - size.width / 2, grid, snap),
    y: snapTo(at.y - size.height / 2, grid, snap),
    width: size.width,
    height: size.height,
    rotation: 0,
    zIndex: topZIndex(scene) + 1,
    locked: false,
    visible: true,
    groupId: null,
    label: null,
    styleJson: null,
    metadataJson: null,
  };
}

export function duplicateElements(
  scene: Scene,
  ids: readonly string[],
  offset: number,
): { scene: Scene; ids: string[] } {
  const set = new Set(ids);
  const groupRemap = new Map<string, string>();
  const copies: SceneElement[] = [];
  let z = topZIndex(scene);

  for (const element of scene.elements) {
    if (!set.has(element.id)) continue;
    if (element.groupId && !groupRemap.has(element.groupId)) {
      groupRemap.set(element.groupId, newGroupId());
    }
    z += 1;
    copies.push({
      ...element,
      id: newLocalId(),
      x: element.x + offset,
      y: element.y + offset,
      zIndex: z,
      // A copy of a linked table must not claim the same table (§38).
      entityType: null,
      entityId: null,
      groupId: element.groupId ? (groupRemap.get(element.groupId) ?? null) : null,
    });
  }

  return { scene: addElements(scene, copies), ids: copies.map((copy) => copy.id) };
}

// ---------------------------------------------------------------------------
// Real entities ⇄ map elements (§38 — the two must stay referentially linked)
// ---------------------------------------------------------------------------

/**
 * A real table or resource a map element may stand for. The editor works from
 * these rather than from free-floating shapes: a shape you draw and then hunt
 * for an entity to attach is how a floor ends up with tables that show no
 * occupancy.
 */
export interface PlaceableEntity {
  id: string;
  kind: MapEntityType;
  label: string;
  areaId: string;
  areaName: string;
  /** Null when the entity has no area, which also means no floor. */
  floorId: string | null;
  /** Seats for a table, capacity for a resource. */
  capacity: number | null;
}

export function elementTypeFor(kind: MapEntityType): MapElementType {
  return kind === 'VenueTable' ? MapElementType.TABLE : MapElementType.RESOURCE;
}

/** A six-seater should not land the same size as a two-seater. */
export function sizeForEntity(entity: PlaceableEntity): { width: number; height: number } {
  if (entity.kind !== 'VenueTable') return elementDefaultSize.RESOURCE;
  const seats = entity.capacity ?? 4;
  if (seats <= 2) return { width: 60, height: 60 };
  if (seats <= 4) return { width: 80, height: 80 };
  if (seats <= 6) return { width: 110, height: 80 };
  if (seats <= 8) return { width: 140, height: 90 };
  return { width: 170, height: 100 };
}

/** Every entity the scene already claims — one element per entity, never two. */
export function placedEntityIds(scene: Scene): Set<string> {
  return new Set(
    scene.elements
      .filter((element) => element.entityId)
      .map((element) => element.entityId as string),
  );
}

export type LinkState = 'linked' | 'unlinked' | 'dangling';

/**
 * `null` for an element that is not meant to carry a link at all (a wall, a
 * door). `dangling` and `unlinked` are deliberately distinct: one is a table
 * that was deleted underneath the map, the other is a shape nobody ever
 * attached — they need different fixes.
 */
export function linkStateOf(
  element: SceneElement,
  knownEntityIds: ReadonlySet<string>,
): LinkState | null {
  if (!isLinkable(element.elementType)) return null;
  if (!element.entityId) return 'unlinked';
  return knownEntityIds.has(element.entityId) ? 'linked' : 'dangling';
}

const PLACE_GAP = 16;

/**
 * The first spot on the canvas where `size` fits without touching anything, so
 * placing eight tables in a row does not stack them all on the same pixel.
 */
export function freeSpotFor(
  scene: Scene,
  size: { width: number; height: number },
  grid: number,
): Point {
  const step = Math.max(grid > 0 ? grid : 20, 20);
  const occupied = scene.elements
    .map((element) => boundsOf([element]))
    .filter((rect): rect is Rect => rect !== null)
    .map((rect) => ({
      x: rect.x - PLACE_GAP,
      y: rect.y - PLACE_GAP,
      width: rect.width + PLACE_GAP * 2,
      height: rect.height + PLACE_GAP * 2,
    }));

  const maxX = Math.max(PLACE_GAP, scene.width - size.width - PLACE_GAP);
  const maxY = Math.max(PLACE_GAP, scene.height - size.height - PLACE_GAP);

  for (let y = PLACE_GAP; y <= maxY; y += step) {
    for (let x = PLACE_GAP; x <= maxX; x += step) {
      const candidate = { x, y, ...size };
      if (!occupied.some((rect) => rectsIntersect(rect, candidate))) return { x, y };
    }
  }

  // A full canvas still has to put the element somewhere findable rather than
  // refuse the placement — below everything else, where it can be dragged in.
  const all = boundsOf(scene.elements);
  return { x: PLACE_GAP, y: all ? all.y + all.height + PLACE_GAP : PLACE_GAP };
}

/**
 * A map element that is linked from the moment it exists. `label` stays null on
 * purpose: the name is read back through the entity, so renaming a table in
 * setup renames it on the map instead of leaving a stale caption behind.
 */
export function createLinkedElement(
  entity: PlaceableEntity,
  scene: Scene,
  grid: number,
  snap: boolean,
  at?: Point,
): SceneElement {
  const size = sizeForEntity(entity);
  const origin = at
    ? { x: at.x - size.width / 2, y: at.y - size.height / 2 }
    : freeSpotFor(scene, size, grid);

  return {
    id: newLocalId(),
    elementType: elementTypeFor(entity.kind),
    entityType: entity.kind,
    entityId: entity.id,
    x: snapTo(origin.x, grid, snap),
    y: snapTo(origin.y, grid, snap),
    width: size.width,
    height: size.height,
    rotation: 0,
    zIndex: topZIndex(scene) + 1,
    locked: false,
    visible: true,
    groupId: null,
    label: null,
    styleJson: null,
    metadataJson: null,
  };
}

/** Places a whole batch, each one in the next free spot. */
export function placeEntities(
  scene: Scene,
  entities: readonly PlaceableEntity[],
  grid: number,
  snap: boolean,
): { scene: Scene; ids: string[] } {
  let next = scene;
  const ids: string[] = [];
  for (const entity of entities) {
    const element = createLinkedElement(entity, next, grid, snap);
    next = addElements(next, [element]);
    ids.push(element.id);
  }
  return { scene: next, ids };
}

/** Selecting one member of a group selects the group, unless the caller opts out. */
export function expandSelection(
  scene: Scene,
  ids: readonly string[],
  respectGroups: boolean,
): string[] {
  if (!respectGroups) return [...ids];
  const groups = new Set(
    scene.elements.filter((el) => ids.includes(el.id) && el.groupId).map((el) => el.groupId),
  );
  if (groups.size === 0) return [...ids];
  const expanded = new Set(ids);
  for (const element of scene.elements) {
    if (element.groupId && groups.has(element.groupId)) expanded.add(element.id);
  }
  return [...expanded];
}
